import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * MoneyInput — drop-in replacement for <Input type="number"> for monetary /
 * count fields. Formats the displayed value with thousand-separator commas
 * (e.g. 16,914.32) while preserving a clean numeric string in the synthetic
 * onChange event so existing parent handlers using parseFloat/parseInt keep
 * working.
 *
 * Implementation notes:
 *  - When the input is focused, we hold the user's raw typed string locally
 *    (kept as `local`). This preserves intermediate states like "1234567."
 *    that a parent parseFloat() would otherwise collapse to "1234567".
 *  - On blur, we clear the local buffer so the display follows the parent
 *    value again (re-formatted cleanly).
 *
 * The "$" lives INSIDE the control (Jun 2026). It is a non-selectable,
 * non-editable prefix element sitting inside the field's border, NOT part of
 * the input's value — the value stays a clean number string and the comma /
 * caret behaviour is unchanged. Every call site used to hand-roll its own
 * symbol next to the field and several never did, so the money field showed
 * no currency at all. One control, one owner of the symbol.
 *
 * Props:
 *  - value: number | string (the "clean" numeric value, no commas)
 *  - onChange: (e) => void where e.target.value is the clean numeric string
 *  - allowDecimals: default true
 *  - showSymbol: default TRUE. Pass false for non-currency numbers (counts,
 *    hours, capacity) — those must never render a "$".
 *  - wrapperClassName / symbolClassName: sizing + tint for the prefix shell
 *  - min, placeholder, className, data-testid, etc. forwarded to <input>
 */
const stripCommas = (s) => String(s ?? "").replace(/,/g, "")

const formatWithCommas = (raw, allowDecimals = true) => {
  if (raw === "" || raw === null || raw === undefined) return ""
  let s = String(raw)
  const neg = s.startsWith("-")
  s = s.replace(/[^0-9.]/g, "")
  if (!allowDecimals) s = s.replace(/\./g, "")
  const firstDot = s.indexOf(".")
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "")
  }
  const [intPart = "", decPart] = s.split(".")
  const intClean = intPart.replace(/^0+(?=\d)/, "")
  const intFormatted = intClean.replace(/\B(?=(\d{3})+(?!\d))/g, ",") || (s.startsWith(".") ? "0" : "")
  let out = intFormatted
  if (s.includes(".")) out += "." + (decPart || "")
  return (neg ? "-" : "") + out
}

const MoneyInput = React.forwardRef(function MoneyInput(
  {
    className,
    value,
    onChange,
    onFocus,
    onBlur,
    allowDecimals = true,
    showSymbol = true,
    wrapperClassName,
    symbolClassName,
    placeholder,
    style,
    ...rest
  },
  ref
) {
  const innerRef = React.useRef(null)
  React.useImperativeHandle(ref, () => innerRef.current)

  // Local raw text kept while focused to preserve in-progress input.
  const [local, setLocal] = React.useState(null)

  const parentClean = stripCommas(value)
  const parentFormatted = formatWithCommas(parentClean, allowDecimals)
  const display = local !== null ? local : parentFormatted

  const emit = (cleanStr, ev) => {
    const synthetic = {
      ...ev,
      target: { ...ev.target, value: cleanStr, name: ev.target?.name },
      currentTarget: { ...ev.currentTarget, value: cleanStr, name: ev.currentTarget?.name },
    }
    onChange && onChange(synthetic)
  }

  const handleChange = (e) => {
    const input = e.target
    const prevDisplay = input.value
    const caret = input.selectionStart ?? prevDisplay.length

    // Sanitize: keep digits, optional single dot (if decimals allowed), leading minus
    let clean = stripCommas(prevDisplay).replace(/[^0-9.\-]/g, "")
    if (!allowDecimals) clean = clean.replace(/\./g, "")
    // collapse multiple dots
    const firstDot = clean.indexOf(".")
    if (firstDot !== -1) {
      clean = clean.slice(0, firstDot + 1) + clean.slice(firstDot + 1).replace(/\./g, "")
    }

    const nextDisplay = formatWithCommas(clean, allowDecimals)

    // Caret: count non-comma chars left of old caret and reposition in new
    const nonCommaBefore = prevDisplay.slice(0, caret).replace(/,/g, "").length
    let newCaret = 0, seen = 0
    while (newCaret < nextDisplay.length && seen < nonCommaBefore) {
      if (nextDisplay[newCaret] !== ",") seen++
      newCaret++
    }

    setLocal(nextDisplay)
    emit(clean, e)

    requestAnimationFrame(() => {
      if (innerRef.current && document.activeElement === innerRef.current) {
        try { innerRef.current.setSelectionRange(newCaret, newCaret) } catch (_) {}
      }
    })
  }

  const handleFocus = (e) => {
    setLocal(parentFormatted)
    onFocus && onFocus(e)
  }

  const handleBlur = (e) => {
    setLocal(null) // snap back to parent-derived format
    onBlur && onBlur(e)
  }

  const field = (
    <input
      {...rest}
      ref={innerRef}
      type="text"
      inputMode={allowDecimals ? "decimal" : "numeric"}
      autoComplete="off"
      value={display}
      placeholder={placeholder}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={style}
      className={cn(
        "flex h-9 w-full rounded-md border border-white/10 bg-black/30 px-3 py-1 text-base text-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] focus-visible:bg-black/40 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        showSymbol && "pl-7",
        className
      )}
    />
  )

  if (!showSymbol) return field

  return (
    <span className={cn("relative inline-flex w-full items-center", wrapperClassName)}>
      <span
        aria-hidden="true"
        data-money-symbol="true"
        className={cn(
          "pointer-events-none absolute left-3 select-none text-[13px] leading-none text-white/40",
          symbolClassName
        )}
      >
        $
      </span>
      {field}
    </span>
  )
})

export { MoneyInput }
export default MoneyInput
